import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User, Palette, Bell, CreditCard, Shield, Sun, Moon, Monitor,
  Gem, Zap, LogOut, KeyRound, ArrowRight, Check, Loader2, Mail, Gift, Copy, Users, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { useTutorial } from "@/lib/tutorial";
import DeleteAccount from "@/components/DeleteAccount";

const TABS = [
  { key: "profile", label: "Profile", icon: User },
  { key: "appearance", label: "Appearance", icon: Palette },
  { key: "invite", label: "Invite & earn", icon: Gift },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "account", label: "Account & Plan", icon: CreditCard },
];

const NOTIF_KEYS = [
  { key: "streak_reminders", label: "Daily streak reminders", desc: "Nudge me before my streak resets." },
  { key: "marketplace_sales", label: "Marketplace sales", desc: "Tell me when someone imports my pack." },
  { key: "room_invites", label: "Study room invites", desc: "Alert me when I'm invited to a room." },
];

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const { start: startTutorial } = useTutorial();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");

  const [fullName, setFullName] = useState(user?.full_name || "");
  const [goal, setGoal] = useState(user?.daily_goal_xp || 100);
  const [savingProfile, setSavingProfile] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [notifs, setNotifs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sv_notifs") || "{}"); } catch { return {}; }
  });

  const [ref, setRef] = useState(null);
  useEffect(() => {
    api.get("/referral/me").then(({ data }) => setRef(data)).catch(() => {});
  }, []);

  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);
  const applyReferral = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/referral/apply", { code: applyCode.trim() });
      toast.success(`+${data.gems_awarded} gems added!`, { icon: "💎" });
      await refresh();
      const r = await api.get("/referral/me");
      setRef(r.data);
      setApplyCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply that code");
    } finally { setApplying(false); }
  };

  const startTour = () => { nav("/app"); setTimeout(() => startTutorial(), 400); };

  const copyRef = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { full_name: fullName.trim(), daily_goal_xp: Number(goal) });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setSavingProfile(false); }
  };

  const rawPlan = user?.raw_plan || "free";
  const displayPlan = rawPlan === "free" ? "free" : (user?.plan || "free");

  const toggleNotif = (k) => {
    const next = { ...notifs, [k]: !(notifs[k] ?? true) };
    setNotifs(next);
    try { localStorage.setItem("sv_notifs", JSON.stringify(next)); } catch {}
    const label = NOTIF_KEYS.find((n) => n.key === k)?.label || "Notification";
    toast.success(`${label} ${next[k] ? "turned on" : "turned off"}`);
  };

  const sendPasswordReset = async () => {
    setResetting(true);
    try {
      await api.post("/auth/forgot-password", { email: user.email });
      toast.success("Password reset link sent to your email.");
    } catch {
      toast.error("Couldn't send reset email");
    } finally { setResetting(false); }
  };

  const themeOptions = [
    { key: "light", label: "Light", icon: Sun },
    { key: "dark", label: "Dark", icon: Moon },
  ];

  return (
    <div className="max-w-[1080px] mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-12" data-testid="settings-page">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-8">
        <div className="eyebrow">Settings</div>
        <h1 className="display-3 mt-2">Your control room</h1>
        <p className="text-[14px] text-[#B0AEBC] mt-2">Tune your profile, look & feel, and account.</p>
      </motion.div>

      <div className="grid md:grid-cols-[220px_1fr] gap-6 md:gap-10">
        {/* Tab nav */}
        <nav className="flex md:flex-col gap-1.5 overflow-x-auto no-scrollbar md:overflow-visible -mx-1 px-1">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`settings-tab-${t.key}`}
                className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13.5px] font-medium whitespace-nowrap transition-colors flex-shrink-0"
                style={{
                  background: active ? "rgba(124,92,255,0.14)" : "transparent",
                  border: active ? "1px solid rgba(124,92,255,0.32)" : "1px solid transparent",
                  color: active ? "var(--foreground)" : undefined,
                }}
              >
                <t.icon size={15} className={active ? "text-[#B8A4FF]" : "text-[#8E8B99]"} />
                <span className={active ? "" : "text-[#B0AEBC]"}>{t.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <div className="min-w-0">
          {tab === "profile" && (
            <Section title="Profile" desc="How you appear across Studivault.">
              <Field label="Full name">
                <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)}
                  maxLength={60} data-testid="settings-name-input" />
              </Field>
              <Field label="Email"><input className="input opacity-60" value={user?.email || ""} disabled /></Field>
              <Field label={`Daily XP goal — ${goal} XP`}>
                <input type="range" min={50} max={500} step={10} value={goal}
                  onChange={(e) => setGoal(e.target.value)} className="w-full accent-[#7C5CFF]"
                  data-testid="settings-goal-slider" />
              </Field>
              <button onClick={saveProfile} disabled={savingProfile} className="btn btn-primary px-6 py-2.5 mt-2" data-testid="settings-save-profile">
                {savingProfile ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save changes
              </button>
            </Section>
          )}

          {tab === "appearance" && (
            <Section title="Appearance" desc="Choose how Studivault looks. Your choice is saved on this device.">
              <div className="grid grid-cols-2 gap-3 max-w-md">
                {themeOptions.map((o) => {
                  const active = theme === o.key;
                  return (
                    <button key={o.key} onClick={() => setTheme(o.key)} data-testid={`theme-option-${o.key}`}
                      className="rounded-2xl p-5 text-left transition-all surface"
                      style={active ? { borderColor: "rgba(124,92,255,0.5)", boxShadow: "0 0 0 1px rgba(124,92,255,0.35), 0 0 30px -8px rgba(124,92,255,0.5)" } : {}}>
                      <o.icon size={20} className="text-[#B8A4FF] mb-3" />
                      <div className="font-semibold text-[15px]">{o.label}</div>
                      <div className="text-[12px] text-[#8E8B99] mt-0.5">{active ? "Active" : "Tap to use"}</div>
                    </button>
                  );
                })}
              </div>
              {user?.active_accent && (
                <div className="mt-6 chip chip-purple">Active accent: {user.active_accent}</div>
              )}

              <div className="mt-8 surface p-5 max-w-md" data-testid="tutorial-restart-card">
                <div className="text-[14px] font-medium">Product tour</div>
                <div className="text-[12.5px] text-[#8E8B99] mt-0.5 mb-3">Replay the quick guided walkthrough of Studivault.</div>
                <button onClick={startTour} className="btn btn-secondary px-5 py-2.5 text-[13px]" data-testid="restart-tutorial-btn">
                  <Sparkles size={14} /> Restart tour
                </button>
              </div>
            </Section>
          )}

          {tab === "invite" && (
            <Section title="Invite friends, earn gems" desc="Share your link. When a friend signs up and verifies their email, you both get gems.">
              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                <Stat label="Friends joined" value={ref?.referral_count ?? 0} icon={Users} />
                <Stat label="Gems earned" value={ref?.gems_earned ?? 0} icon={Gem} />
                <Stat label="They get" value={`+${ref?.reward_referee ?? 15}`} icon={Gift} />
              </div>

              <div className="surface p-5 max-w-xl" data-testid="referral-card">
                <div className="text-[12px] font-medium text-[#B0AEBC] mb-2">Your referral code</div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 input font-mono text-[18px] tracking-[0.3em] font-bold text-center text-[#B8A4FF]">
                    {ref?.code || "------"}
                  </div>
                  <button onClick={() => copyRef(ref?.code || "")} className="btn btn-secondary px-4 py-3" data-testid="copy-ref-code" aria-label="Copy code">
                    <Copy size={15} />
                  </button>
                </div>
                <div className="text-[12px] font-medium text-[#B0AEBC] mb-2">Share link</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 input text-[12.5px] truncate text-[#B0AEBC]">{ref?.share_url || "…"}</div>
                  <button onClick={() => copyRef(ref?.share_url || "")} className="btn btn-primary px-4 py-3" data-testid="copy-ref-link">
                    <Copy size={15} /> Copy
                  </button>
                </div>
                <p className="text-[12px] text-[#8E8B99] mt-4 leading-relaxed">
                  You earn <span className="text-[#B8A4FF] font-semibold">+{ref?.reward_referrer ?? 20} gems</span> for every friend who verifies their email,
                  and they start with <span className="text-[#B8A4FF] font-semibold">+{ref?.reward_referee ?? 15} bonus gems</span>. Fair, unlimited, no catch.
                </p>
              </div>

              {ref && !ref.referred && (
                <div className="surface p-5 max-w-xl mt-4" data-testid="apply-referral-card">
                  <div className="text-[14px] font-medium mb-1">Have a friend's code?</div>
                  <div className="text-[12.5px] text-[#8E8B99] mb-3">Enter it once to grab +{ref?.reward_referee ?? 15} bonus gems.</div>
                  <div className="flex items-center gap-2">
                    <input
                      value={applyCode}
                      onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                      maxLength={6}
                      placeholder="ABC123"
                      data-testid="apply-ref-input"
                      className="input flex-1 font-mono tracking-[0.2em] uppercase placeholder:tracking-normal"
                    />
                    <button onClick={applyReferral} disabled={applying || applyCode.trim().length < 4}
                      className="btn btn-primary px-5 py-3 disabled:opacity-50" data-testid="apply-ref-btn">
                      {applying ? <Loader2 size={15} className="animate-spin" /> : "Apply"}
                    </button>
                  </div>
                </div>
              )}
            </Section>
          )}

          {tab === "notifications" && (
            <Section title="Notifications" desc="Control what we ping you about.">
              <div className="space-y-3">
                {NOTIF_KEYS.map((n) => {
                  const on = notifs[n.key] ?? true;
                  return (
                    <div key={n.key} className="surface p-4 flex items-center justify-between gap-4" data-testid={`notif-${n.key}`}>
                      <div>
                        <div className="text-[14px] font-medium">{n.label}</div>
                        <div className="text-[12.5px] text-[#8E8B99] mt-0.5">{n.desc}</div>
                      </div>
                      <button onClick={() => toggleNotif(n.key)} data-testid={`notif-toggle-${n.key}`}
                        className="w-11 h-6 rounded-full flex-shrink-0 transition-colors relative"
                        style={{ background: on ? "#7C5CFF" : "rgba(128,128,150,0.35)" }}>
                        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                          style={{ left: on ? "22px" : "2px" }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {tab === "account" && (
            <Section title="Account & Plan" desc="Your subscription, security and account actions.">
              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                <Stat label="Plan" value={displayPlan.toUpperCase()} icon={CreditCard} />
                <Stat label="Gems" value={user?.gems ?? 0} icon={Gem} />
                <Stat label="Level" value={user?.level ?? 1} icon={Zap} />
              </div>

              <Row onClick={() => nav("/app/billing")} icon={ArrowRight} title="Manage plan & billing"
                desc={displayPlan !== "free" ? `You're on ${displayPlan[0].toUpperCase()}${displayPlan.slice(1)}`
                  : user?.plan !== "free" ? "You're on Free — all Pro features are unlocked during Free Mode"
                  : "Upgrade to Plus or Pro"} testId="settings-billing" />
              <Row onClick={() => nav("/app/security")} icon={Shield} title="Two-factor authentication"
                desc={user?.totp_enabled ? "Enabled" : "Add an extra layer of security"} testId="settings-2fa" />
              <Row onClick={sendPasswordReset} icon={resetting ? Loader2 : KeyRound} title="Change password"
                desc="We'll email you a secure reset link" testId="settings-password" loading={resetting} />

              {user?.email_verified === false && (
                <div className="surface p-4 mt-3 flex items-center gap-3" style={{ borderColor: "rgba(245,158,11,0.3)" }}>
                  <Mail size={16} className="text-[#F59E0B]" />
                  <span className="text-[13px] text-[#B0AEBC]">Your email isn't verified yet. Check your inbox for the link.</span>
                </div>
              )}

              <div className="mt-8 rounded-2xl p-5" style={{ border: "1px solid rgba(252,165,165,0.3)", background: "rgba(252,165,165,0.05)" }}>
                <div className="text-[13px] font-semibold text-[#FCA5A5] mb-1">Danger zone</div>
                <p className="text-[12.5px] text-[#8E8B99] mb-3">Signing out ends this session on this device.</p>
                <div className="flex flex-wrap gap-2.5">
                  <button onClick={logout} className="btn btn-secondary px-5 py-2.5 text-[13px]" data-testid="settings-logout">
                    <LogOut size={14} /> Sign out
                  </button>
                  <DeleteAccount user={user} onDeleted={logout} />
                </div>
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, desc, children }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
    <h2 className="text-[20px] font-semibold">{title}</h2>
    <p className="text-[13px] text-[#8E8B99] mt-1 mb-6">{desc}</p>
    <div className="space-y-4">{children}</div>
  </motion.div>
);

const Field = ({ label, children }) => (
  <div className="max-w-md">
    <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, icon: Icon }) => (
  <div className="surface p-4">
    <Icon size={15} className="text-[#B8A4FF] mb-2" />
    <div className="text-[12px] text-[#8E8B99]">{label}</div>
    <div className="text-[20px] font-bold tabular-nums">{value}</div>
  </div>
);

const Row = ({ onClick, icon: Icon, title, desc, testId, loading }) => (
  <button onClick={onClick} disabled={loading} data-testid={testId}
    className="surface surface-hover w-full p-4 flex items-center justify-between gap-4 text-left mb-3">
    <div>
      <div className="text-[14px] font-medium">{title}</div>
      <div className="text-[12.5px] text-[#8E8B99] mt-0.5">{desc}</div>
    </div>
    <Icon size={16} className={`text-[#8E8B99] flex-shrink-0 ${loading ? "animate-spin" : ""}`} />
  </button>
);
